// The site's sections, as the page editor uses them.
//
// Two halves of one decision, in one file because they have to agree: what a
// section picker OFFERS, and what choosing from it DOES to the element that
// now points at the section. The picker exists in two places — the add modal,
// where a `section` element is created, and that element's own Select tab,
// where it is repointed later — and picking in one has to mean what picking in
// the other means.

// Options for a section picker, in the shape the `select` widget wants.
//
// A function rather than an array because form-schema calls `options` with the
// resolved property. Callers memoise the result on `sections`: the returned
// closure is what the form's `config` is built from, and a fresh identity
// there rebuilds the widget tree beneath the form, detaching the control being
// typed into (P139).
export const sectionOptions = (sections) => () => [
  { value: "", label: "Select a section" },
  ...sections.map(({ sectionId }) => ({ value: sectionId, label: sectionId })),
];

// The section a picker just named, out of the list the editor already holds.
//
// `undefined` for a section that is not in the list, which is a real state: the
// list is one page of 100 and a page may point at a section created since it
// was read. Every caller treats that as "no copy to make" rather than an error
// — the element keeps the id it was given, and the renderer is what says
// "Section not found".
export const findSection = (sections, sectionId) =>
  sectionId
    ? sections.find((section) => section.sectionId === sectionId)
    : undefined;

// What an element takes FROM the section it has just been pointed at.
//
// gen-2 copied the section's name, frame and tags onto the element on select,
// and `common/schema/section/fields` still describes `frame` that way — "copied
// onto a page element when the section is selected there and then allowed to
// diverge, which is how one section renders as a header on one page and inline
// on another". This is where that copy happens; until it existed the sentence
// described nothing.
//
// A partial rather than a whole element: the callers merge it, and merging is
// what lets this stay silent about the fields it has no opinion on.
//
// Two of the three are conditional, and for the same reason: a copy that
// REPLACES something a person typed is worse than no copy at all.
//
//   name   only when the element has none. It is the handle a template reaches
//          another element by (`cxt.elements` is keyed on it), so a section's
//          id is a good default and a poor override.
//   tags   only when the section has some AND the element has none. Tags decide
//          WHERE the element lands — "page:header", "page:footer" — so a
//          section with none would otherwise silently move an element into the
//          body, and a person who tagged it in the dialog meant that too.
//   frame  whenever the section's differs from the element's. This is the one
//          the picker really decides: `frame` is on no dialog and on no tab the
//          picker shares, so there is never anything of a person's to
//          overwrite, and on the Select tab choosing a different section is a
//          deliberate act the frame should follow.
//
// So applying it twice is applying it once — an empty patch the second time,
// for every field. That is what lets the add path run it again as a safety net
// and know it changed nothing; see withSection.
export const sectionPatch = (element, section) => {
  const patch = {};

  if (!element.name) {
    patch.name = section.sectionId;
  }

  if (section.frame && section.frame !== element.frame) {
    patch.frame = section.frame;
  }

  if (section.tags && section.tags.length && !(element.tags || []).length) {
    patch.tags = [...section.tags];
  }

  return patch;
};

// The cascade the add dialog hangs off its `config` field, so that picking a
// section fills the element in while the dialog is still open.
//
// One field writing its siblings, which is a thing form-schema provides rather
// than a trick: the widget dispatcher hands a `config.onChange` handler a
// `parent` whose `values` are the object the field lives in and whose
// `setValues` writes it, tagged so the widgets it rewrote are remounted and
// show what arrived (widgets/widget.js).
//
// It hangs off `config` and not off `sectionId`, and that is what decides where
// any such handler goes: `sectionId` lives inside the resolved oneOf branch, so
// its parent is the branch, and a handler there could write nothing but the
// branch.
//
// It is also what makes the dialog submittable: `name` is `/Name`, whose
// pattern an empty string fails, so Add is disabled until something fills it.
//
// It fires on every change to `config` — including the reset that choosing a
// type performs — so it asks whether a section was actually named before it
// touches anything.
export const sectionCascade = (sections) => ({ value, previous, parent }) => {
  const sectionId = value ? value.sectionId : null;

  if (!sectionId || sectionId === (previous ? previous.sectionId : null)) {
    return;
  }

  const section = findSection(sections, sectionId);

  if (!section) {
    return;
  }

  const patch = sectionPatch(parent.values, section);

  if (Object.keys(patch).length > 0) {
    parent.setValues(patch);
  }
};

// The same copy, applied to the element as it is ADDED — and the one that
// decides, because the cascade above cannot be relied on alone.
//
// Every widget write in form-schema is a whole-object merge over the snapshot
// that widget captured, so a control remounted by the cascade and then writing
// its own value back can carry a pre-cascade snapshot with it. The fields that
// survive that are exactly the ones with a widget of their own to re-assert
// them — `name` and `tags`, both controls in this dialog — and `frame` has
// none: it is on no dialog. Measured on br-w01 against one unchanged bundle,
// three runs in four added the element with its name and tags copied and its
// frame still the default, which is the shape that gives the mechanism away.
//
// By the time this runs the dialog has closed, the element is in the page's
// array, and the only widget left in the tree is the list itself, which writes
// nothing of its own. `sectionPatch` treats what it has already written as
// already there, so running it twice writes the same element as running it
// once.
export const withSection = (element, sections) => {
  if (!element || element.type !== "section") {
    return element;
  }

  const section = findSection(sections, (element.config || {}).sectionId);

  if (!section) {
    return element;
  }

  const patch = sectionPatch(element, section);

  return Object.keys(patch).length > 0 ? { ...element, ...patch } : element;
};
